import { OPS_AUTH, USE_MOCK_AUTH } from "../config/api";
import { formatAccountStatus, normalizeOpsUser, normalizePhoneNumber } from "../utils/normalizeOpsUser";
import { cleanGhanaPhone } from "../utils/ghanaPhone";
import { flattenPermissionKeys } from "../config/permissions";
import { apiRequest, unwrapApiEnvelope } from "./apiClient";

/**
 * Normalize a team-member row for the Team table / manage modal.
 * Uses the same Admin field names as Redux (`last_login_at`, `phone_number`, …).
 */
export function normalizeTeamMember(raw) {
  const base = normalizeOpsUser({
    ...raw,
    permissions: flattenPermissionKeys(raw?.permissions).length
      ? flattenPermissionKeys(raw.permissions)
      : raw?.permissions,
  });
  if (!base) return null;

  const statusLabel = raw?.deleted_at
    ? "deleted"
    : formatAccountStatus(raw?.status);

  return {
    ...base,
    id: base.id != null ? String(base.id) : base.admin_slug || "",
    numericId: base.id != null && Number.isFinite(Number(base.id)) ? Number(base.id) : null,
    admin_slug: base.admin_slug,
    phone: normalizePhoneNumber(base.phone_number),
    status: statusLabel,
    invitedAt: base.created_at ? String(base.created_at).slice(0, 10) : null,
    last_login_at: base.last_login_at ?? null,
    deleted_at: base.deleted_at ?? null,
    permissions: base.permissions?.length
      ? base.permissions
      : flattenPermissionKeys(raw?.permissions),
  };
}

/**
 * Team member payloads may arrive as an array, a paginated `{ data: [...] }`,
 * or a keyed object. Pull the operator rows out of any of those shapes.
 */
export function extractTeamMemberList(data) {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== "object") return [];

  for (const key of ["data", "team_members", "teamMembers", "members", "items", "results"]) {
    if (Array.isArray(data[key])) return data[key];
  }

  const values = Object.values(data);
  if (values.length && values.every((v) => v && typeof v === "object" && !Array.isArray(v))) {
    return values.filter((v) => v.email || v.admin_slug || v.first_name);
  }
  return [];
}

let teamCache = { token: null, promise: null };

/**
 * GET /api/ops/all/team_member
 * Concurrent callers (incl. StrictMode double-mount) share one request.
 */
export async function fetchTeamMembers(token) {
  if (USE_MOCK_AUTH || !token) {
    return null;
  }

  if (teamCache.token === token && teamCache.promise) {
    return teamCache.promise;
  }

  const promise = (async () => {
    const json = await apiRequest(`${OPS_AUTH.teamMembers}?include_deleted=1&limit=100`, {
      method: "GET",
      token,
    });
    const data = unwrapApiEnvelope(json);
    const rows = extractTeamMemberList(data);

    if (!rows.length && data && typeof data !== "object") {
      console.warn("[teamMembers] unrecognized payload shape", data);
    }

    return rows.map(normalizeTeamMember).filter(Boolean);
  })();

  teamCache = { token, promise };

  try {
    return await promise;
  } finally {
    if (teamCache.promise === promise) {
      teamCache = { token: null, promise: null };
    }
  }
}

export function clearTeamMembersCache() {
  teamCache = { token: null, promise: null };
}

/**
 * POST /api/ops/auth/register
 * Body: { first_name, last_name, email, phone_number, role_slug }
 */
export async function registerTeamMember(token, payload) {
  const cleanedPhone = cleanGhanaPhone(payload.phone_number);
  const body = {
    first_name: payload.first_name?.trim(),
    last_name: payload.last_name?.trim() || null,
    email: payload.email?.trim().toLowerCase(),
    phone_number: cleanedPhone || String(payload.phone_number ?? "").replace(/^\+/, "").trim(),
    role_slug: payload.role_slug,
  };

  if (!body.first_name || !body.email || !body.phone_number || !body.role_slug) {
    throw new Error("First name, email, phone number, and role are required.");
  }

  if (USE_MOCK_AUTH || !token) {
    return normalizeTeamMember({
      ...body,
      admin_slug: `local_${Date.now()}`,
      id: Date.now(),
      status: true,
      role: payload.role || null,
      permissions: payload.permissions || [],
      created_at: new Date().toISOString(),
    });
  }

  const json = await apiRequest(OPS_AUTH.register, {
    method: "POST",
    token,
    body,
  });

  return normalizeTeamMember(unwrapApiEnvelope(json));
}

/**
 * PUT /api/ops/update/team_member
 * Body: { admin_slug, first_name, last_name, email, phone_number, role_slug }
 */
export async function updateTeamMember(token, payload) {
  const cleanedPhone = cleanGhanaPhone(payload.phone_number);
  const body = {
    admin_slug: payload.admin_slug,
    first_name: payload.first_name?.trim(),
    last_name: payload.last_name?.trim() || null,
    email: payload.email?.trim().toLowerCase(),
    phone_number: cleanedPhone || String(payload.phone_number ?? "").replace(/^\+/, "").trim(),
    role_slug: payload.role_slug,
  };

  if (!body.admin_slug) {
    throw new Error("Team member slug is required.");
  }

  if (USE_MOCK_AUTH || !token) {
    return normalizeTeamMember({
      ...body,
      id: payload.id,
      status: payload.status ?? true,
      role: payload.role || null,
      permissions: payload.permissions || [],
      created_at: payload.created_at ?? null,
    });
  }

  const json = await apiRequest(OPS_AUTH.updateTeamMember, {
    method: "PUT",
    token,
    body,
  });

  return normalizeTeamMember(unwrapApiEnvelope(json));
}

/**
 * POST /api/ops/status/team_member
 * Body: { admin_slug, status: 'active' | 'inactive' }
 */
export async function updateTeamMemberStatus(token, adminSlug, status) {
  if (!adminSlug) throw new Error("Team member slug is required.");
  const next = status === true || status === "active" ? "active" : "inactive";

  if (USE_MOCK_AUTH || !token) {
    return normalizeTeamMember({
      admin_slug: adminSlug,
      status: next === "active",
    });
  }

  const json = await apiRequest(OPS_AUTH.statusTeamMember, {
    method: "POST",
    token,
    body: { admin_slug: adminSlug, status: next },
  });

  return normalizeTeamMember(unwrapApiEnvelope(json));
}

/**
 * POST /api/ops/destroy/team_member
 * Body: { admin_slug }
 */
export async function deleteTeamMember(token, adminSlug) {
  if (!adminSlug) {
    throw new Error("Team member slug is required.");
  }

  if (USE_MOCK_AUTH || !token) {
    return true;
  }

  await apiRequest(OPS_AUTH.deleteTeamMember, {
    method: "POST",
    token,
    body: { admin_slug: adminSlug },
  });

  return true;
}

/**
 * POST /api/ops/restore/team_member
 * Body: { admin_slug }
 */
export async function restoreTeamMember(token, adminSlug) {
  if (!adminSlug) {
    throw new Error("Team member slug is required.");
  }

  if (USE_MOCK_AUTH || !token) {
    return normalizeTeamMember({
      admin_slug: adminSlug,
      status: true,
      deleted_at: null,
    });
  }

  const json = await apiRequest(OPS_AUTH.restoreTeamMember, {
    method: "POST",
    token,
    body: { admin_slug: adminSlug },
  });

  return normalizeTeamMember(unwrapApiEnvelope(json));
}
